import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowRight, FaGlobe, FaRocket } from 'react-icons/fa';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 1.2rem 1rem 0;
`;

const Shell = styled(motion.div)`
  position: relative;
  max-width: 1220px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 34px 34px 0 0;
  padding: 2.4rem 1.2rem 2.2rem;
  background:
    radial-gradient(circle at 10% 10%, rgba(251, 113, 133, 0.22), transparent 28%),
    radial-gradient(circle at 100% 10%, rgba(59, 130, 246, 0.2), transparent 28%),
    linear-gradient(135deg, #200d16 0%, #65152a 32%, #0f2d57 100%);
  box-shadow: 0 -10px 40px rgba(15, 23, 42, 0.12);

  @media (min-width: 900px) {
    padding: 3rem 2rem 2.8rem;
  }
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1.4rem;
  align-items: center;

  @media (min-width: 920px) {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 0.75fr);
  }
`;

const Copy = styled.div`
  color: #ffffff;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.82rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #ffd2dc;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Title = styled.h2`
  margin: 1rem 0 0;
  font-size: clamp(2rem, 3.8vw, 3.2rem);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.04em;
  color: #fff7ed;
`;

const Description = styled.p`
  margin: 0.9rem 0 0;
  max-width: 640px;
  color: rgba(241, 245, 249, 0.84);
  line-height: 1.8;
  font-size: 1rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 1.45rem;
`;

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  text-decoration: none;
  padding: 0.95rem 1.3rem;
  border-radius: 16px;
  background: linear-gradient(90deg, #fb7185 0%, #f97316 100%);
  color: #ffffff;
  font-weight: 800;
  box-shadow: 0 18px 36px rgba(249, 115, 22, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 40px rgba(249, 115, 22, 0.34);
  }
`;

const SecondaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  text-decoration: none;
  padding: 0.95rem 1.25rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #ffffff;
  font-weight: 700;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SummaryCard = styled.div`
  padding: 1.2rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(14px);
  color: #ffffff;
`;

const SummaryTitle = styled.div`
  font-size: 0.84rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #fde68a;
`;

const SummaryHeadline = styled.div`
  margin-top: 0.85rem;
  font-size: 1.5rem;
  line-height: 1.2;
  font-weight: 800;
  color: #ffffff;
`;

const SummaryText = styled.div`
  margin-top: 0.65rem;
  color: rgba(226, 232, 240, 0.82);
  line-height: 1.7;
  font-size: 0.92rem;
`;

const Checklist = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ChecklistRow = styled.div`
  display: flex;
  gap: 0.65rem;
  align-items: center;
  padding: 0.78rem 0.88rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(241, 245, 249, 0.9);
  font-size: 0.88rem;
  font-weight: 600;
`;

const CTASection = () => {
  return (
    <Section id="cta">
      <Shell
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.6 }}
      >
        <Grid>
          <Copy>
            <Eyebrow>
              <FaRocket />
              Ready To Launch
            </Eyebrow>
            <Title>Bring your school onto a platform that feels complete from day one</Title>
            <Description>
              From operations and communication to websites and admissions, Skhool is designed to help your team move
              with confidence instead of patching together multiple systems.
            </Description>

            <Actions>
              <PrimaryAction href="https://app.skhool.in/register-school">
                Start School Setup
                <FaArrowRight />
              </PrimaryAction>
              <SecondaryAction href="https://webdemo.skhool.in/">
                <FaGlobe />
                Open Demo Website
              </SecondaryAction>
            </Actions>
          </Copy>

          <SummaryCard>
            <SummaryTitle>What happens next</SummaryTitle>
            <SummaryHeadline>Go from first click to a working digital school flow faster</SummaryHeadline>
            <SummaryText>
              Start with school setup, configure key modules, and publish your website presence without switching tools.
            </SummaryText>

            <Checklist>
              <ChecklistRow>School setup and academic structure guidance</ChecklistRow>
              <ChecklistRow>Website, announcements, and public presence in one platform</ChecklistRow>
              <ChecklistRow>Admin, teacher, and parent workflows working together</ChecklistRow>
            </Checklist>
          </SummaryCard>
        </Grid>
      </Shell>
    </Section>
  );
};

export default CTASection;
